import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Form } from "../../components/Form";
import { InfoBox } from "../../components/InfoBox";
import { useTranslation } from "react-i18next";
import { useUpdateStoreUserPasswordMutation } from "../../query/usersQuery";
import type { StoreId, UserId } from "@KassaroAB/shared/types/ids";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const Description = styled.p`
  margin: 0 0 ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

interface ChangePasswordFormProps {
  storeId: StoreId | null;
  userId: UserId;
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ChangePasswordForm = ({
  storeId,
  userId,
  email,
  onSuccess,
  onCancel,
}: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const {
    mutate: updatePassword,
    error,
    isPending,
  } = useUpdateStoreUserPasswordMutation(storeId);

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    onSubmit: ({ value }) => {
      updatePassword(
        { userId, data: { password: value.password } },
        {
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
        },
      );
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {error && (
        <InfoBox variant="error">
          {error?.message ?? t("changePasswordForm.errorChange")}
        </InfoBox>
      )}

      <Description>{t("changePasswordForm.description", { email })}</Description>

      <form.Field
        name="password"
        validators={{
          onChange: ({ value }) => {
            if (!value) return t("changePasswordForm.passwordRequired");
            if (value.length < 6) return t("changePasswordForm.passwordMinLength");
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("changePasswordForm.passwordLabel")}</FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("changePasswordForm.passwordPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="confirmPassword"
        validators={{
          onChangeListenTo: ["password"],
          onChange: ({ value, fieldApi }) => {
            if (value !== fieldApi.form.getFieldValue("password")) {
              return t("changePasswordForm.confirmMismatch");
            }
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("changePasswordForm.confirmLabel")}</FormLabel>
            <Input
              type="password"
              autoComplete="new-password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("changePasswordForm.confirmPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isPending}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? t("changePasswordForm.saving") : t("changePasswordForm.save")}
        </Button>
      </FormActions>
    </Form>
  );
};
